import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Receipt, Users, Search, Filter, ListChecks, RefreshCw, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, X, Send } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;
const MONTH_NAMES = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];

const GenerateBilling = () => {
  const navigate = useNavigate();
  const today = new Date();

  // ===== Settings (left panel) =====
  const [mode, setMode] = useState("specific"); // "all" | "specific"
  const [periodMonth, setPeriodMonth] = useState(today.getMonth() + 1);
  const [periodYear, setPeriodYear] = useState(today.getFullYear());
  const [dueDateType, setDueDateType] = useState("default"); // "default" | "custom"
  const [customDueDate, setCustomDueDate] = useState("");
  const [description, setDescription] = useState("");
  const [broadcastWA, setBroadcastWA] = useState(false);
  const [skipExisting, setSkipExisting] = useState(true);

  // ===== Customer list (right panel) =====
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [branches, setBranches] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [loadedAll, setLoadedAll] = useState(false);

  // ===== Submission =====
  const [submitting, setSubmitting] = useState(false);
  const [resultModal, setResultModal] = useState(null);

  // Auto description on period change
  useEffect(() => {
    if (!description || description.startsWith("Biaya Langganan Internet Bulan ")) {
      setDescription(`Biaya Langganan Internet Bulan ${MONTH_NAMES[periodMonth-1]} ${periodYear}`);
    }
  // eslint-disable-next-line
  }, [periodMonth, periodYear]);

  // Load branches (best-effort)
  useEffect(() => {
    const token = localStorage.getItem("token");
    axios.get(`${API_URL}/api/v2/branches?is_active=true`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const data = r.data?.data || r.data || [];
        setBranches(Array.isArray(data) ? data : []);
      })
      .catch(() => setBranches([]));
  }, []);

  const fetchCustomers = async (loadAll = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ per_page: loadAll ? 5000 : 500, is_active: "true" });
      if (search && search.length >= 2) params.set("search", search);
      if (branchFilter) params.set("branch_id", branchFilter);
      const r = await axios.get(`${API_URL}/api/v2/customers?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = r.data?.data || r.data?.items || [];
      setAllCustomers(Array.isArray(data) ? data : []);
      setLoadedAll(loadAll);
      setPage(1);
    } catch (e) {
      toast.error("Gagal memuat pelanggan: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(false); /* eslint-disable-next-line */ }, []);

  // Debounced search refetch
  useEffect(() => {
    const t = setTimeout(() => fetchCustomers(loadedAll), 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line
  }, [search, branchFilter]);

  // ===== Filtered + paginated view =====
  const filtered = allCustomers; // backend already filtered
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      currentPageItems.forEach(c => next.add(c.id));
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds(new Set(filtered.map(c => c.id)));
    toast.success(`Memilih ${filtered.length} pelanggan (semua hasil filter)`);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const canSubmit = mode === "all" || selectedIds.size > 0;

  const handleSubmit = async () => {
    if (mode === "specific" && selectedIds.size === 0) {
      toast.error("Pilih minimal 1 pelanggan");
      return;
    }
    if (dueDateType === "custom" && !customDueDate) {
      toast.error("Pilih tanggal jatuh tempo khusus");
      return;
    }
    const customerCount = mode === "all" ? filtered.length : selectedIds.size;
    if (!window.confirm(`Generate ${mode === "all" ? "SEMUA" : selectedIds.size} tagihan untuk periode ${MONTH_NAMES[periodMonth-1]} ${periodYear}?\\n\\n${broadcastWA ? "✓ Broadcast WhatsApp AKTIF" : "(Tanpa broadcast WA)"}`)) {
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        period_month: periodMonth,
        period_year: periodYear,
        mode,
        customer_ids: mode === "specific" ? Array.from(selectedIds) : null,
        branch_id: mode === "all" ? (branchFilter || null) : null,
        due_date_type: dueDateType,
        custom_due_date: dueDateType === "custom" ? customDueDate : null,
        description,
        skip_existing: skipExisting,
        broadcast_whatsapp: broadcastWA,
      };
      const r = await axios.post(`${API_URL}/api/v2/invoices/bulk-generate`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000,  // 5 menit untuk bulk besar
      });
      if (r.data?.success) {
        setResultModal(r.data.data);
        toast.success(`${r.data.data.generated_count} tagihan tercipta, ${r.data.data.skipped_count} skip, ${r.data.data.failed_count} gagal`);
        // Reset selection for next session
        setSelectedIds(new Set());
      } else {
        toast.error(r.data?.message || "Gagal generate");
      }
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6 animate-fade-in min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="page-generate-billing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="btn-back">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Receipt className="w-6 h-6 text-indigo-600 dark:text-indigo-400" /> Generate Tagihan
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Buat tagihan baru untuk pelanggan secara otomatis</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate("/app/invoices")} data-testid="btn-back-invoices">
          <ArrowLeft className="w-4 h-4 mr-2" /> Kembali
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ============ LEFT: SETTINGS ============ */}
        <Card className="lg:col-span-1 border-0 shadow-lg h-fit" data-testid="card-settings">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">📝 Pengaturan Tagihan</CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">Konfigurasi pengaturan tagihan yang akan dibuat</p>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Tipe Generate */}
            <div>
              <label className="block text-sm font-medium mb-2">Tipe Generate</label>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${mode === "all" ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700 hover:border-slate-300"}`} data-testid="radio-mode-all">
                  <input type="radio" name="mode" value="all" checked={mode === "all"} onChange={() => setMode("all")} className="mt-1" />
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Semua Pelanggan</div>
                    <div className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">Generate untuk semua pelanggan aktif{branchFilter ? " (sesuai filter cabang)" : ""}</div>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${mode === "specific" ? "border-indigo-500 bg-slate-900 text-white dark:bg-slate-800" : "border-slate-200 dark:border-slate-700 hover:border-slate-300"}`} data-testid="radio-mode-specific">
                  <input type="radio" name="mode" value="specific" checked={mode === "specific"} onChange={() => setMode("specific")} className="mt-1" />
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2"><ListChecks className="w-4 h-4" /> Pelanggan Tertentu</div>
                    <div className={`text-xs mt-0.5 ${mode === "specific" ? "text-slate-300" : "text-slate-500"}`}>Pilih pelanggan secara spesifik</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Periode */}
            <div>
              <label className="block text-sm font-medium mb-2">Bulan Pembayaran</label>
              <div className="text-xs text-slate-500 mb-2 flex items-center gap-1 dark:text-slate-400">📅 {MONTH_NAMES[periodMonth-1]} {periodYear}</div>
              <div className="grid grid-cols-2 gap-2">
                <select value={periodMonth} onChange={e => setPeriodMonth(Number(e.target.value))} className="px-3 py-2 border rounded-md bg-white dark:bg-slate-800 text-sm" data-testid="select-period-month">
                  {MONTH_NAMES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
                <input type="number" value={periodYear} onChange={e => setPeriodYear(Number(e.target.value))} className="px-3 py-2 border rounded-md bg-white dark:bg-slate-800 text-sm" min={2020} max={2100} data-testid="input-period-year" />
              </div>
            </div>

            {/* Tenggat */}
            <div>
              <label className="block text-sm font-medium mb-2">Tenggat Pembayaran</label>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${dueDateType === "default" ? "border-indigo-500 bg-slate-900 text-white dark:bg-slate-800" : "border-slate-200 dark:border-slate-700"}`} data-testid="radio-due-default">
                  <input type="radio" name="due" value="default" checked={dueDateType === "default"} onChange={() => setDueDateType("default")} className="mt-1" />
                  <div>
                    <div className="font-semibold text-sm">Default Pelanggan</div>
                    <div className={`text-xs mt-0.5 ${dueDateType === "default" ? "text-slate-300" : "text-slate-500"}`}>Jatuh tempo dari default data pelanggan</div>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${dueDateType === "custom" ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700 border-dashed"}`} data-testid="radio-due-custom">
                  <input type="radio" name="due" value="custom" checked={dueDateType === "custom"} onChange={() => setDueDateType("custom")} className="mt-1" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">📅 Tanggal Khusus</div>
                    <div className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">jatuh tempo spesifik</div>
                    {dueDateType === "custom" && (
                      <input type="date" value={customDueDate} onChange={e => setCustomDueDate(e.target.value)} className="mt-2 w-full px-2 py-1 text-sm border rounded bg-white dark:bg-slate-800" data-testid="input-custom-due-date" />
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Deskripsi */}
            <div>
              <label className="block text-sm font-medium mb-2">Deskripsi Tagihan</label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Biaya Langganan Internet..." data-testid="input-description" />
              <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">Deskripsi akan otomatis mengikuti perubahan periode</div>
            </div>

            {/* Options */}
            <div className="space-y-2 pt-2 border-t">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="check-skip-existing">
                <input type="checkbox" checked={skipExisting} onChange={e => setSkipExisting(e.target.checked)} className="mt-0.5" />
                <div className="text-xs">
                  <div className="font-medium">Skip duplikat (rekomendasi)</div>
                  <div className="text-slate-500 dark:text-slate-400">Lewati pelanggan yang sudah punya tagihan periode ini</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="check-broadcast-wa">
                <input type="checkbox" checked={broadcastWA} onChange={e => setBroadcastWA(e.target.checked)} className="mt-0.5" />
                <div className="text-xs">
                  <div className="font-medium flex items-center gap-1"><Send className="w-3 h-3" /> Broadcast tagihan</div>
                  <div className="text-slate-500 dark:text-slate-400">Centang untuk mengirim notifikasi WhatsApp otomatis</div>
                </div>
              </label>
            </div>

            {/* Submit */}
            <Button onClick={handleSubmit} disabled={!canSubmit || submitting} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="btn-submit-generate">
              {submitting ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Memproses...</>) : (<><Receipt className="w-4 h-4 mr-2" /> Generate {mode === "all" ? "Semua" : `${selectedIds.size} Pelanggan`}</>)}
            </Button>
          </CardContent>
        </Card>

        {/* ============ RIGHT: CUSTOMER LIST ============ */}
        <Card className="lg:col-span-2 border-0 shadow-lg" data-testid="card-customer-list">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Pilih Pelanggan</CardTitle>
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">{mode === "all" ? "Mode SEMUA: semua pelanggan aktif akan diproses (filter cabang opsional)" : "Pilih pelanggan yang akan dibuatkan tagihan"}</p>
              </div>
              <div className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 text-xs font-semibold" data-testid="badge-selected-count">
                {mode === "all" ? "Mode SEMUA" : `${selectedIds.size} dipilih`}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Toolbar */}
            <div className="space-y-3 mb-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama, ID, atau nomor telepon pelanggan..." className="pl-10" data-testid="input-search-customer" />
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)} className="px-3 py-2 border rounded-md text-sm bg-white dark:bg-slate-800" data-testid="select-branch-filter">
                  <option value="">Semua Wilayah</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name || b.branch_name || b.id}</option>)}
                </select>
                <Button variant="outline" size="sm" onClick={() => fetchCustomers(true)} disabled={loading} data-testid="btn-load-all"><Users className="w-3 h-3 mr-1" /> Load Semua</Button>
                <Button variant="outline" size="sm" onClick={selectAllFiltered} disabled={mode === "all" || filtered.length === 0} data-testid="btn-select-all"><ListChecks className="w-3 h-3 mr-1" /> Pilih Semua</Button>
                {selectedIds.size > 0 && <Button variant="outline" size="sm" onClick={clearSelection} data-testid="btn-clear-selection"><X className="w-3 h-3 mr-1" /> Bersihkan</Button>}
                <Button variant="outline" size="sm" onClick={() => fetchCustomers(loadedAll)} disabled={loading} data-testid="btn-refresh-customers"><RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
              </div>
            </div>

            {/* Customer Grid */}
            {mode === "all" ? (
              <div className="text-center py-12 text-slate-400 dark:text-slate-600 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg" data-testid="all-mode-info">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <div className="text-sm font-medium">Mode SEMUA PELANGGAN aktif</div>
                <div className="text-xs mt-1">Tagihan akan dibuat untuk semua pelanggan aktif {branchFilter ? "di cabang terpilih" : "(semua cabang)"}.</div>
                <div className="text-xs mt-1">Estimasi: {filtered.length} pelanggan</div>
              </div>
            ) : loading ? (
              <div className="text-center py-12 text-slate-400 dark:text-slate-500"><RefreshCw className="w-6 h-6 mx-auto animate-spin" /></div>
            ) : currentPageItems.length === 0 ? (
              <div className="text-center py-12 text-slate-400 dark:text-slate-500"><Users className="w-10 h-10 mx-auto mb-2 opacity-50" /><div className="text-sm">Tidak ada pelanggan</div></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="grid-customers">
                {currentPageItems.map(c => {
                  const sel = selectedIds.has(c.id);
                  return (
                    <label key={c.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${sel ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700 hover:border-slate-300"}`} data-testid={`customer-row-${c.id}`}>
                      <input type="checkbox" checked={sel} onChange={() => toggleSelect(c.id)} className="mt-1" data-testid={`customer-checkbox-${c.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{c.name}</div>
                        <div className="text-xs text-slate-500 truncate dark:text-slate-400">{c.account_number || c.pppoe_username || "-"}</div>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {c.service_plan_name && <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs rounded">{c.service_plan_name}</span>}
                          {(c.branch_name || c.branch_id) && <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded">{c.branch_name || c.branch_id}</span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {mode === "specific" && filtered.length > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-xs text-slate-500 dark:text-slate-400">Menampilkan {(page-1)*PAGE_SIZE + 1}–{Math.min(page*PAGE_SIZE, filtered.length)} dari {filtered.length} pelanggan</div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllVisible} disabled={currentPageItems.every(c => selectedIds.has(c.id))} data-testid="btn-select-page">Pilih Halaman</Button>
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p-1))} data-testid="btn-page-prev"><ChevronLeft className="w-3 h-3" /></Button>
                  <span className="text-xs">Hal {page} / {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p+1))} data-testid="btn-page-next"><ChevronRight className="w-3 h-3" /></Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ============ RESULT MODAL ============ */}
      {resultModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="modal-result">
          <div className="fixed inset-0 bg-black/50" onClick={() => setResultModal(null)} />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Hasil Generate</h2>
              <button onClick={() => setResultModal(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" data-testid="btn-close-result"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded text-center" data-testid="result-generated">
                  <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{resultModal.generated_count}</div>
                  <div className="text-xs text-emerald-600 dark:text-emerald-400">Berhasil</div>
                </div>
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded text-center" data-testid="result-skipped">
                  <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">{resultModal.skipped_count}</div>
                  <div className="text-xs text-amber-600 dark:text-amber-400">Skip (dupe)</div>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded text-center" data-testid="result-failed">
                  <div className="text-2xl font-bold text-red-700 dark:text-red-300">{resultModal.failed_count}</div>
                  <div className="text-xs text-red-600 dark:text-red-400">Gagal</div>
                </div>
              </div>
              {resultModal.failed_count > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-600 dark:text-red-400" /> Gagal:</div>
                  <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                    {resultModal.failed.map(f => <div key={f.customer_id} className="px-2 py-1 bg-red-50 dark:bg-red-900/20 rounded">{f.customer_name}: <span className="text-red-700 dark:text-red-300">{f.error}</span></div>)}
                  </div>
                </div>
              )}
              {resultModal.skipped_count > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold mb-1">Skipped (duplikat):</div>
                  <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                    {resultModal.skipped.slice(0, 50).map(s => <div key={s.customer_id} className="px-2 py-1 bg-amber-50 dark:bg-amber-900/20 rounded">{s.customer_name} — {s.reason}</div>)}
                    {resultModal.skipped.length > 50 && <div className="text-slate-500 text-center dark:text-slate-400">... dan {resultModal.skipped.length - 50} lainnya</div>}
                  </div>
                </div>
              )}
              {resultModal.generated_count > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1 text-emerald-700 dark:text-emerald-300">Sukses ({resultModal.generated.length}):</div>
                  <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                    {resultModal.generated.slice(0, 100).map(g => <div key={g.invoice_id} className="px-2 py-1 bg-emerald-50 dark:bg-emerald-900/20 rounded flex justify-between"><span>{g.customer_name}</span><span className="font-mono text-emerald-700 dark:text-emerald-300">{g.invoice_number}</span></div>)}
                    {resultModal.generated.length > 100 && <div className="text-slate-500 text-center dark:text-slate-400">... dan {resultModal.generated.length - 100} lainnya</div>}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResultModal(null)} data-testid="btn-result-close">Tutup</Button>
              <Button onClick={() => navigate("/app/invoices")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-go-invoices">Lihat di Daftar Tagihan</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateBilling;
